import { useState } from "react";
import { Link } from "react-router-dom";
import { Activity } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { SESSIONS } from "@/lib/trading";

export default function AuthPage({ mode }) {
  const { authenticate } = useAuth();
  const isLogin = mode === "login";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await authenticate(isLogin ? "/auth/login" : "/auth/register", isLogin ? { email: form.email, password: form.password } : form);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <section className="hidden lg:flex flex-col justify-between p-12 border-r border-slate-800 bg-[#0D1119]">
        <div className="flex items-center gap-2"><Activity className="h-5 w-5 text-emerald-400" /><span className="font-display font-bold text-xl">TradePulse</span></div>
        <div className="rise">
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-4">Session-aware trading journal</p>
          <h1 className="font-display text-5xl font-extrabold leading-[1.05] tracking-tight max-w-lg">Find the session<br />that pays you.</h1>
          <p className="text-slate-400 mt-6 max-w-md">Log every entry, see your P&L split across Asian, London and New York sessions, total your daily earnings, and mirror master accounts to slaves.</p>
          <div className="mt-10 space-y-3 max-w-md">
            {SESSIONS.map((s, i) => (
              <div key={s.key} className="flex items-center gap-3 font-mono text-xs">
                <span className="w-20 text-slate-400">{s.label}</span>
                <div className="relative flex-1 h-2 rounded bg-slate-800/70">
                  <div className="absolute h-2 rounded rise" style={{ left: `${(s.start / 24) * 100}%`, width: `${((s.end - s.start) / 24) * 100}%`, background: s.color, animationDelay: `${i * 120}ms` }} />
                </div>
                <span className="w-20 text-right text-slate-500">{s.hours}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="font-mono text-xs text-slate-600">UTC · 24h market clock</p>
      </section>
      <section className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 rise" data-testid="auth-form">
          <div>
            <h2 className="font-display text-3xl font-bold">{isLogin ? "Sign in" : "Create account"}</h2>
            <p className="text-sm text-slate-400 mt-1">{isLogin ? "Welcome back, trader." : "Choose your own password to get started."}</p>
          </div>
          {!isLogin && (
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Your name" /></div>
          )}
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="auth-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input data-testid="auth-password-input" type="password" required minLength={isLogin ? 1 : 8} value={form.password} onChange={set("password")} placeholder={isLogin ? "••••••••" : "At least 8 characters"} /></div>
          {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid={isLogin ? "login-submit-button" : "register-submit-button"} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
            {loading ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </Button>
          <p className="text-sm text-slate-400">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="text-emerald-400 hover:underline" data-testid="auth-switch-link">{isLogin ? "Create an account" : "Sign in"}</Link>
          </p>
        </form>
      </section>
    </div>
  );
}
